import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

export type ColorMode = "light" | "dark";

interface ColorModeContextType {
  mode: ColorMode;
  isDark: boolean;
  setMode: (mode: ColorMode) => void;
  toggleColorMode: () => void;
}

const STORAGE_KEY = "agendepro-color-mode";

const ColorModeContext = createContext<ColorModeContextType | undefined>(
  undefined
);

function readStoredMode(): ColorMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "dark" || stored === "light") {
      return stored;
    }
  } catch {
    // Ignore storage errors (private mode, etc.)
  }
  return "light";
}

function applyModeClass(mode: ColorMode) {
  document.documentElement.classList.toggle("dark", mode === "dark");
}

export const ColorModeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [mode, setModeState] = useState<ColorMode>(() =>
    typeof window === "undefined" ? "light" : readStoredMode()
  );

  useLayoutEffect(() => {
    applyModeClass(mode);
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Ignore storage errors
    }
  }, [mode]);

  useLayoutEffect(() => {
    return () => {
      document.documentElement.classList.remove("dark");
    };
  }, []);

  const setMode = useCallback((next: ColorMode) => {
    setModeState(next);
  }, []);

  const toggleColorMode = useCallback(() => {
    setModeState((current) => (current === "dark" ? "light" : "dark"));
  }, []);

  const value = useMemo(
    () => ({
      mode,
      isDark: mode === "dark",
      setMode,
      toggleColorMode,
    }),
    [mode, setMode, toggleColorMode]
  );

  return (
    <ColorModeContext.Provider value={value}>
      {children}
    </ColorModeContext.Provider>
  );
};

export const useColorMode = (): ColorModeContextType => {
  const context = useContext(ColorModeContext);
  if (!context) {
    throw new Error("useColorMode must be used within a ColorModeProvider");
  }
  return context;
};
